/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { lazy, Suspense, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, TriangleAlertIcon } from 'lucide-react';
import type { ShopperBasketsV2, ShopperPromotions } from '@/scapi';
import type { ProductMapResult } from '@/lib/product/product-map';
import { ToggleCard, ToggleCardEdit, ToggleCardSummary } from '@/components/toggle-card';
import { Button } from '@/components/buttons';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { UITarget } from '@/targets/ui-target';
import { formatCurrency } from '@/lib/currency';
import type { useCustomerProfile } from '@/hooks/checkout/use-customer-profile';
import { useKountReady } from '@/providers/kount-ready';
import CheckoutErrorBanner from './checkout-error-banner';
import TermsAndConditions from './terms-and-conditions';
import { CHECKOUT_STEPS, type CheckoutStep } from '../utils/checkout-context-types';
import { recomposeShopperVisibleTotal } from '@/lib/basket/recompose-total';

const OrderReviewItems = lazy(() => import('./order-review-items'));

// ---------------------------------------------------------------------------
// OrderReviewStep
// ---------------------------------------------------------------------------

interface OrderReviewStepProps {
    step: CheckoutStep;
    productMapPromise: Promise<ProductMapResult>;
    promotionsPromise?: Promise<Record<string, ShopperPromotions.schemas['Promotion']>>;
    cart: ShopperBasketsV2.schemas['Basket'] | null | undefined;
    customerProfile: ReturnType<typeof useCustomerProfile>;
    currency: string;
    showCaliforniaMattressAlert: boolean;
    placeOrderError?: string;
    onPlaceOrderSubmit: (e: FormEvent<HTMLFormElement>) => void;
    isPlacingOrder: boolean;
    isPlaceOrderPending: boolean;
    isSubmittingPayment: boolean;
    isPaymentFetcherSubmitting: boolean;
    freshnessPending: boolean;
    freshnessError: string | null;
    className?: string;
}

/**
 * CHECKOUT-14 — Order review step card.
 * Shown once the shopper reaches the payment step. In the "upcoming" state
 * (payment is the active step) the card renders a grayed heading + placeholder
 * text. Once payment is submitted and the flow advances to PLACE_ORDER, the
 * card expands to reveal the read-only item list, optional account-creation
 * prompt, Terms & Conditions gate, and the Place Order CTA.
 *
 * Mirrors the collapsed / expanded ToggleCard pattern used by ContactInfo,
 * ShippingAddress, ShippingOptions, and Payment.
 */
export default function OrderReviewStep({
    step,
    productMapPromise,
    promotionsPromise,
    cart,
    customerProfile: _customerProfile,
    currency,
    showCaliforniaMattressAlert,
    placeOrderError,
    onPlaceOrderSubmit,
    isPlacingOrder,
    isPlaceOrderPending,
    isSubmittingPayment,
    isPaymentFetcherSubmitting,
    freshnessPending,
    freshnessError,
    className,
}: OrderReviewStepProps) {
    const { t, i18n } = useTranslation('checkout');
    // CHECKOUT-17 — Place Order CTA stays disabled until the Kount DDC SDK has
    // collected (or the 5s timeout has elapsed). `useKountReady()` defaults to
    // `true` outside `<KountReadyProvider>`, so this gate is a no-op when Kount
    // is not wired (Storybook, non-checkout pages).
    const kountReady = useKountReady();

    const isActive = step >= CHECKOUT_STEPS.PLACE_ORDER;
    const isUpcoming = step < CHECKOUT_STEPS.PLACE_ORDER;

    // Figma parity: the order total is rendered inside the Place Order CTA label
    // (e.g. "PLACE ORDER - $1,033.28"), replacing the previously separate Total
    // headline above the button.
    const formattedTotal = formatCurrency(recomposeShopperVisibleTotal(cart), i18n.language, currency);
    return (
        <ToggleCard
            id="order-review"
            title={t('reviewOrder.heading')}
            editing={isActive}
            disableEdit={isUpcoming}
            className={className}>
            <ToggleCardSummary>
                <p className="text-sm text-muted-foreground" data-testid="review-upcoming">
                    {t('reviewOrder.upcoming')}
                </p>
            </ToggleCardSummary>

            <ToggleCardEdit>
                <div className="flex flex-col items-end gap-4 w-full">
                    {/* Read-only items grouped by delivery method */}
                    <div className="w-full">
                        <Suspense fallback={null}>
                            <OrderReviewItems
                                productMapPromise={productMapPromise}
                                promotionsPromise={promotionsPromise}
                            />
                        </Suspense>
                    </div>

                    {showCaliforniaMattressAlert ? (
                        <Alert className="border-0 border-t-2 border-t-warning shadow-lg rounded-md bg-card [&>svg]:text-warning">
                            <TriangleAlertIcon />
                            <AlertTitle>{t('mattressRecycling.alert.title')}</AlertTitle>
                            <AlertDescription>
                                <p>{t('mattressRecycling.alert.subtitle')}</p>
                                <p>
                                    {t('mattressRecycling.alert.programDescription')}{' '}
                                    <a
                                        href={t('mattressRecycling.alert.programLinkHref')}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline underline-offset-4">
                                        {t('mattressRecycling.alert.programLinkLabel')}
                                    </a>
                                    .
                                </p>
                                <p>
                                    <a
                                        href={t('mattressRecycling.alert.definitionLinkHref')}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline underline-offset-4">
                                        {t('mattressRecycling.alert.definitionLinkLabel')}
                                    </a>{' '}
                                    {t('mattressRecycling.alert.definitionLinkSuffix')}
                                </p>
                                <p>{t('mattressRecycling.alert.contaminationNotice')}</p>
                            </AlertDescription>
                        </Alert>
                    ) : null}

                    {/* Figma parity legal disclosure: implied consent text with links. */}
                    <div className="w-full">
                        <TermsAndConditions />
                    </div>

                    {placeOrderError ? <CheckoutErrorBanner message={placeOrderError} className="w-full" /> : null}
                    {!placeOrderError && freshnessError ? (
                        <CheckoutErrorBanner message={freshnessError} className="w-full" />
                    ) : null}

                    <UITarget targetId="checkout.placeOrder.before" />
                    <UITarget targetId="checkout.placeOrder">
                        <form
                            onSubmit={onPlaceOrderSubmit}
                            className="fixed bottom-[var(--ot-banner-height,0px)] left-0 right-0 z-50 border-t border-border bg-background px-6 py-4 lg:static lg:inset-auto lg:z-auto lg:w-full lg:border-0 lg:bg-transparent lg:p-0">
                            <Button
                                type="submit"
                                disabled={
                                    !kountReady ||
                                    freshnessPending ||
                                    Boolean(freshnessError) ||
                                    isPlacingOrder ||
                                    isPlaceOrderPending ||
                                    isSubmittingPayment ||
                                    isPaymentFetcherSubmitting
                                }
                                data-testid="place-order-button"
                                className="h-12 w-full whitespace-normal bg-foreground text-primary-foreground uppercase hover:bg-foreground/90">
                                <Lock className="size-4" />
                                {freshnessPending
                                    ? t('placeOrder.validating', { defaultValue: 'Validating availability...' })
                                    : isPlacingOrder || isPlaceOrderPending || isSubmittingPayment
                                      ? t('placeOrder.processing')
                                      : t('placeOrder.button', { total: formattedTotal })}
                            </Button>
                        </form>
                    </UITarget>
                    <UITarget targetId="checkout.placeOrder.after" />
                </div>
            </ToggleCardEdit>
        </ToggleCard>
    );
}
